import React from 'react';

interface YouTubeEmbedProps {
  videoId: string;
  title?: string;
  className?: string;
  aspectRatio?: '16:9' | '4:3' | '1:1';
  lazyLoad?: boolean;
}

// Helper function to extract video ID from various YouTube URL formats
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;
  
  // Handle youtu.be format
  if (url.includes('youtu.be/')) {
    const id = url.split('youtu.be/')[1]?.split(/[?&#]/)[0];
    return id || null;
  }
  
  // Handle youtube.com/watch?v= format
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/\?v=)([^?&#]+)/);
  if (watchMatch) {
    return watchMatch[1] || null;
  }
  
  // If the input is already just a video ID (11 characters)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return url;
  }
  
  return null;
}

export default function YouTubeEmbed({ 
  videoId, 
  title = 'YouTube video player', 
  className = '', 
  aspectRatio = '16:9',
  lazyLoad = true
}: YouTubeEmbedProps) {
  // Ensure we have a clean video ID
  const cleanVideoId = extractYouTubeVideoId(videoId) || videoId;
  
  // Calculate padding based on aspect ratio
  const getPaddingBottom = () => {
    switch (aspectRatio) {
      case '4:3': return '75%';
      case '1:1': return '100%';
      case '16:9':
      default: return '56.25%';
    }
  };

  return (
    <div className={`relative w-full ${className}`} style={{ paddingBottom: getPaddingBottom() }}>
      <iframe
        className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md"
        src={`https://www.youtube.com/embed/${cleanVideoId}?rel=0`}
        title={title}
        loading={lazyLoad ? "lazy" : "eager"}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    </div>
  );
}